/**
 * Brand mark shared with the device's local settings page: a satellite orbiting
 * a disk, plus the "DDiskSatellite" wordmark (accent on the bold "Satellite").
 * Keep this 1:1 with the client identity in device/src/agent.c.
 */

import { useId, type CSSProperties } from 'react';

/** Orbit path for the satellite: exactly the visible ellipse (rx21 ry9, tilt -28°,
 * centre 24,24) traced as four quarter arcs, so the satellite rides the ring. */
const ORBIT_PATH =
  'M42.54 14.14 A21 9 -28 0 1 28.22 31.95 A21 9 -28 0 1 5.46 33.86 ' +
  'A21 9 -28 0 1 19.78 16.05 A21 9 -28 0 1 42.54 14.14 Z';

/** Ride the orbit via CSS motion-path (see @keyframes ddisk-orbit in styles.css).
 * offset-rotate:0 keeps the dot from spinning (matches the old SMIL rotate="0"). */
const ORBIT_STYLE = {
  offsetPath: `path('${ORBIT_PATH}')`,
  offsetRotate: '0deg',
  animation: 'ddisk-orbit 9s linear infinite',
} as CSSProperties;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function Logo({
  size = 38,
  showWordmark = true,
  wordmarkColor,
}: {
  /** Pixel size of the square SVG mark. */
  size?: number;
  /** Render the "DDiskSatellite" text next to the mark. */
  showWordmark?: boolean;
  /** Colour of the non-accent half of the wordmark (defaults to inherit). */
  wordmarkColor?: string;
}) {
  // Unique gradient id so multiple <Logo/> instances (and the inlined favicon)
  // never collide on the same defs id. useId() is stable across renders and
  // SSR-safe, unlike a module counter mutated during render.
  const gid = `ddisk-logo-lg-${useId()}`;
  const reduce = prefersReducedMotion();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#5b8def" />
            <stop offset="1" stopColor="#7c5cff" />
          </linearGradient>
        </defs>
        <ellipse
          cx="24"
          cy="24"
          rx="21"
          ry="9"
          stroke="#5b8def"
          strokeWidth="2"
          opacity=".55"
          transform="rotate(-28 24 24)"
        />
        <circle cx="24" cy="24" r="10" fill={`url(#${gid})`} />
        <circle cx="24" cy="24" r="3.4" fill="#0d1220" />
        {reduce ? (
          <circle cx="42.54" cy="14.14" r="3" fill="#7c5cff" />
        ) : (
          <>
            {/* soft halo + satellite, both riding the orbit in sync via CSS
                motion-path (SMIL animateMotion froze the first cycle in Chromium) */}
            <circle r="5.5" fill="#7c5cff" opacity="0.16" style={ORBIT_STYLE} />
            <circle r="3" fill="#7c5cff" style={ORBIT_STYLE} />
          </>
        )}
      </svg>
      {showWordmark && (
        <span
          className="brand-wm"
          style={{ fontSize: Math.round(size * 0.46), color: wordmarkColor }}
        >
          <span className="brand-wm__txt">
            DDisk<b>Satellite</b>
          </span>
          {!reduce && (
            <span className="brand-wm__shine" aria-hidden="true">
              DDisk<b>Satellite</b>
            </span>
          )}
        </span>
      )}
    </span>
  );
}
